import React, { useState } from 'react';
import DialogModal from '../../../components/DialogModal';
import {
  ConcessionType,
  DateWiseConcession,
  PaymentConcessionMasterProps,
  UpdatePaymentConcessionProps
} from '../types';
import TextInputWithIcon from '../../../components/TextInputWithIcon';
import DropdownSelect from '../../../components/DropdownSelect';
import { applicableOnOptions, concessionTypeOptions } from '../utils/constant';
import { StyledButton } from '../../Exams/styled';
import { CircularProgress } from '@mui/material';
import { isEmpty, isEqual } from 'lodash';
import { PaymentConcessionType, SnackbarSeverityType } from '../../../enums';
import ConfirmationDialog from '../../../components/ConfirmationDialog/ConfirmationDialog';
import { getDefaultSelectedConcessionType } from '../utils/paymentConcessionUtils';
import paymentConcessionMaster from '../../../apis/payments/paymentConcessionMaster';
import DateWiseConcessionTable from './DateWiseConcessionTable';
import { FormWrapper } from '../styled';
import SelectAll from '../../../components/SelectAllField/SelectAllField';

interface EditPaymentConcessionModalProps {
  activeTab: ConcessionType;
  gradeOptions: { label: string; value: string }[];
  isEditPaymentConcessionModalOpen: boolean;
  selectedPaymentConcessionData: PaymentConcessionMasterProps | undefined;
  selectedAcademicYear: string;
  loggedInUser: any;
  fetchPaymentConcessionMaster: () => Promise<void>;
  setIsEditPaymentConcessionModalOpen: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  showToast: (message: string, severity: SnackbarSeverityType) => void;
  allPaymentConcessionMasterGrades: string[];
}

const EditPaymentConcessionModal = ({
  activeTab,
  gradeOptions,
  isEditPaymentConcessionModalOpen,
  selectedPaymentConcessionData,
  selectedAcademicYear,
  loggedInUser,
  fetchPaymentConcessionMaster,
  setIsEditPaymentConcessionModalOpen,
  showToast,
  allPaymentConcessionMasterGrades
}: EditPaymentConcessionModalProps) => {
  const concessionField =
    activeTab === 'rollover'
      ? 'rollOverConcessions'
      : 'newAdmissionConcessions';

  const concessionData = selectedPaymentConcessionData?.[concessionField]?.[0];

  const [grades, setGrades] = useState<string[]>(
    selectedPaymentConcessionData?.grades || []
  );
  const [concessionName, setConcessionName] = useState<string>(
    concessionData?.description || ''
  );
  const [applicableOn, setApplicableOn] = useState<string>(
    concessionData?.applicableOn?.[0] || ''
  );
  const [concessionType, setConcessionType] = useState<string>(
    getDefaultSelectedConcessionType(concessionData) || ''
  );

  const [percentage, setPercentage] = useState<number>(
    concessionData?.fixedPercentage || 0
  );
  const [amount, setAmount] = useState<number>(
    concessionData?.fixedAmount || 0
  );
  const [dateWiseConcession, setDateWiseConcession] = useState<
    DateWiseConcession[]
  >(concessionData?.dateWiseConcession || []);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);
  const [confirmationDialogMessage, setConfirmationDialogMessage] =
    useState('');

  const originalGrades = selectedPaymentConcessionData?.grades || [];

  const globallyUsedOtherGrades = allPaymentConcessionMasterGrades.filter(
    (grade) => !originalGrades.includes(grade)
  );

  const filteredGradeOptions = gradeOptions.filter(
    (grade) =>
      !globallyUsedOtherGrades.includes(grade.value) &&
      !grade.value.includes('-INCOMING')
  );

  const hasGradeDataChanged = !isEqual(
    selectedPaymentConcessionData?.grades,
    grades
  );

  const hasApplicableOnChanged =
    !isEmpty(applicableOn) &&
    !isEqual(concessionData?.applicableOn?.[0], applicableOn);

  const hasConcessionNameChanged =
    !isEmpty(concessionName) &&
    !isEqual(concessionData?.description, concessionName);

  const hasDateWiseConcessionChanged =
    !isEmpty(dateWiseConcession) &&
    !isEqual(concessionData?.dateWiseConcession, dateWiseConcession);

  const hasFixedAmountChanged =
    concessionType === PaymentConcessionType.FIXED_AMOUNT &&
    amount !== 0 &&
    !isEqual(concessionData?.fixedAmount, amount);

  const hasFixedPercentageChanged =
    concessionType === PaymentConcessionType.FIXED_PERCENTAGE &&
    percentage !== 0 &&
    !isEqual(concessionData?.fixedPercentage, percentage);

  const hasDataChanged =
    hasGradeDataChanged ||
    hasApplicableOnChanged ||
    hasConcessionNameChanged ||
    hasDateWiseConcessionChanged ||
    hasFixedAmountChanged ||
    hasFixedPercentageChanged;

  const gradeImpactMessage =
    'The changes made in grade will reflect both in New Admission and Rollover.';
  const unsavedChangesMessage =
    'There are some unsaved changed in the concession. Are you sure you want to exit without saving?';

  const resetData = () => {
    setGrades([]);
    setConcessionName('');
    setApplicableOn('');
    setConcessionType('');
    setPercentage(0);
    setAmount(0);
    setDateWiseConcession([]);
  };

  const dateWiseConcessionData =
    concessionType === PaymentConcessionType.DATE_WISE_AMOUNT
      ? dateWiseConcession.map((concession) => ({
          ...concession,
          percentage: null
        }))
      : concessionType === PaymentConcessionType.DATE_WISE_PERCENTAGE
      ? dateWiseConcession.map((concession) => ({
          ...concession,
          amount: null
        }))
      : [];

  const newAdmissionAndRolloverConcessionData = {
    ...concessionData,
    description: concessionName,
    applicableOn: [applicableOn],
    fixedPercentage:
      concessionType === PaymentConcessionType.FIXED_PERCENTAGE
        ? percentage
        : null,
    fixedAmount:
      concessionType === PaymentConcessionType.FIXED_AMOUNT ? amount : null,
    dateWiseConcession: dateWiseConcessionData
  };

  const handleSave = async () => {
    if (!selectedPaymentConcessionData?.id) return;
    setIsSubmitting(true);
    try {
      const payload: UpdatePaymentConcessionProps = {
        id: selectedPaymentConcessionData?.id,
        grades: grades,
        newAdmissionConcessions:
          activeTab === 'newAdmission'
            ? [newAdmissionAndRolloverConcessionData]
            : undefined,
        rollOverConcessions:
          activeTab === 'rollover'
            ? [newAdmissionAndRolloverConcessionData]
            : undefined,
        applicableOn: !isEmpty(applicableOn) ? [applicableOn] : undefined
      };
      await paymentConcessionMaster.updatePaymentConcessionMaster({
        school: loggedInUser?.school,
        academicYear: selectedAcademicYear,
        payload
      });
      showToast(
        'Successfully updated payment concession grades',
        SnackbarSeverityType.success
      );
    } catch (error: any) {
      showToast(
        error?.errors?.[0].message ||
          'Error updating payment concession grades',
        SnackbarSeverityType.error
      );
    } finally {
      setIsSubmitting(false);
      resetData();
      fetchPaymentConcessionMaster();
      setIsEditPaymentConcessionModalOpen(false);
    }
  };

  const handleModalClose = () => {
    if (hasDataChanged) {
      setConfirmationDialogMessage(unsavedChangesMessage);
      setIsConfirmationDialogOpen(true);
      return;
    }
    resetData();
    setIsEditPaymentConcessionModalOpen(false);
  };

  const handleConfirmDialogCancel = () => {
    setIsConfirmationDialogOpen(false);
  };

  const onMainSaveClick = () => {
    if (hasGradeDataChanged) {
      setConfirmationDialogMessage(gradeImpactMessage);
      setIsConfirmationDialogOpen(true);
    } else {
      handleSave();
    }
  };

  const handleOnDialogConfirm = () => {
    if (confirmationDialogMessage === gradeImpactMessage) {
      handleSave();
      setConfirmationDialogMessage('');
      setIsConfirmationDialogOpen(false);
    } else {
      resetData();
      setConfirmationDialogMessage('');
      setIsEditPaymentConcessionModalOpen(false);
    }
  };

  const isSaveButtonDisabled =
    isSubmitting ||
    !hasDataChanged ||
    isEmpty(grades) ||
    isEmpty(concessionName) ||
    isEmpty(applicableOn);

  return (
    <>
      <DialogModal
        maxWidth='md'
        title={`Edit Full Payment Concession for ${
          activeTab === 'rollover' ? 'Rollover' : 'New Admission'
        }`}
        open={isEditPaymentConcessionModalOpen}
        onClose={handleModalClose}
        actions={
          <StyledButton
            onClick={onMainSaveClick}
            disabled={isSaveButtonDisabled}
          >
            {isSubmitting ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Save'
            )}
          </StyledButton>
        }
      >
        <FormWrapper>
          <SelectAll
            label='Grades'
            placeholder='Select Grades'
            onChange={setGrades}
            options={filteredGradeOptions}
            selected={grades}
            hasTopLabel
          />
          <TextInputWithIcon
            label='Concession Name'
            placeholder='Enter Concession Name'
            value={concessionName}
            onChange={(event) => setConcessionName(event.target.value)}
            hasTopLabel
          />
          <DropdownSelect
            label='Applicable On'
            placeholder='Select Applicable On'
            name='applicableOn'
            onChange={(event) => setApplicableOn(event.target.value as string)}
            options={applicableOnOptions}
            value={applicableOn}
          />
          <DropdownSelect
            label='Concession Type'
            placeholder='Select Concession Type'
            name='concessionType'
            onChange={(event) =>
              setConcessionType(event.target.value as string)
            }
            options={concessionTypeOptions}
            value={concessionType}
          />
          {concessionType === PaymentConcessionType.FIXED_PERCENTAGE && (
            <TextInputWithIcon
              label='Percentage'
              placeholder='Enter Percentage'
              type='tel'
              value={percentage}
              onChange={(event) =>
                setPercentage(Math.max(0, Number(event.target.value)))
              }
              hasTopLabel
              InputProps={{
                inputProps: { min: 0 }
              }}
            />
          )}
          {concessionType === PaymentConcessionType.FIXED_AMOUNT && (
            <TextInputWithIcon
              label='Amount'
              placeholder='Enter Amount'
              type='tel'
              value={amount}
              onChange={(event) =>
                setAmount(Math.max(0, Number(event.target.value)))
              }
              hasTopLabel
              InputProps={{
                inputProps: { min: 0 }
              }}
            />
          )}
          {(concessionType === PaymentConcessionType.DATE_WISE_PERCENTAGE ||
            concessionType === PaymentConcessionType.DATE_WISE_AMOUNT) && (
            <DateWiseConcessionTable
              dateWiseConcession={dateWiseConcession}
              concessionType={concessionType}
              setDateWiseConcession={setDateWiseConcession}
            />
          )}
        </FormWrapper>
      </DialogModal>

      <ConfirmationDialog
        message={confirmationDialogMessage}
        onClose={handleConfirmDialogCancel}
        onConfirm={handleOnDialogConfirm}
        open={isConfirmationDialogOpen}
      />
    </>
  );
};

export default EditPaymentConcessionModal;
